import { SubmitHandler, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Radio,
  RadioGroup,
  Spacer,
  Tag,
  TagCloseButton,
  TagLabel,
  TagRightIcon,
  Text,
  Tooltip,
  VStack,
  Wrap,
} from "@chakra-ui/react";
import {
  DeleteIcon,
  SmallCloseIcon,
  ViewIcon,
  ViewOffIcon,
} from "@chakra-ui/icons";
import { TalesSpinner } from "../../../../atoms";
import { useDispatch, useSelector } from "react-redux";
import { AppThunkDispatch } from "../../../../../store";
import useProgrammi from "../../../../../utils/hooks/useProgrammi";
import {
  getProgrammaData,
  getProgrammaError,
  isProgrammaLoading,
} from "../../../../../store/programma/selectors";
import { addProgramma } from "../../../../../store/programma/actions/add";
import NewEventDetailForm from "./atoms/NewEventDetailForm";
import NewEventForm from "./atoms/NewEventForm";
import { toggleProgrammaVisibility } from "../../../../../store/programma/actions/toggleVisibility";
import { deleteProgramma } from "../../../../../store/programma/actions/delete";
import { editProgramma } from "../../../../../store/programma/actions/edit";
import React from "react";
import { StarIcon } from "@chakra-ui/icons";

interface ProgrammaInput {
  title: string;
  subtitle: string;
  order: number;
  show: boolean;
}

export default function Programmi() {
  useProgrammi();
  const dispatch = useDispatch<AppThunkDispatch>();
  const isPending = useSelector(isProgrammaLoading);
  const programmi = useSelector(getProgrammaData);
  const error = useSelector(getProgrammaError);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    getValues,
  } = useForm<ProgrammaInput>({
    defaultValues: {
      title: "",
      subtitle: "",
      order: (programmi?.length ?? 0) + 1,
      show: true,
    },
  });

  const onSubmit: SubmitHandler<ProgrammaInput> = (data) => {
    const newProgramma = {
      title: getValues("title"),
      subtitle: getValues("subtitle"),
      show: getValues("show"),
      order: getValues("order"),
      events: [],
    };
    dispatch(addProgramma(newProgramma));
  };

  return (
    <>
      {error && (
        <Box>
          <Text my="10px">{error}</Text>
        </Box>
      )}
      <Wrap>
        {!isPending &&
          programmi &&
          programmi?.map((programma) => (
            <Card key={programma.id ?? programma.title} w="md" m="10px">
              <CardHeader>
                <Flex>
                  <VStack alignItems="left">
                    <Heading size="base">{programma.title}</Heading>
                    {programma.subtitle && <Text>{programma.subtitle}</Text>}
                    <Text>Ordine: {programma.order}</Text>
                  </VStack>
                  <Spacer />
                  <StarIcon
                    w={6}
                    h={6}
                    color={programma.highlight ? "talesYellow.500" : "grey"}
                    onClick={() =>
                      dispatch(
                        editProgramma({
                          ...programma,
                          highlight: !programma.highlight,
                        }),
                      )
                    }
                  />
                </Flex>
              </CardHeader>
              <CardBody>
                {programma.events?.map((event, index) => (
                  <Box key={event.title}>
                    <Flex alignItems="center">
                      <Heading size="base" mr="10px">
                        {event.title}
                      </Heading>
                      <Spacer />
                      <Tooltip label={event.show ? "Nascondi" : "Mostra"}>
                        <IconButton
                          mr="5px"
                          aria-label={event.show ? "Nascondi" : "Mostra"}
                          variant="solid"
                          icon={event.show ? <ViewOffIcon /> : <ViewIcon />}
                          colorScheme="gray"
                          onClick={() => {
                            let newProgramma = { ...programma };
                            let newEvents = [...newProgramma.events];
                            let newEvent = { ...newEvents[index] };
                            newEvent.show = !newEvent.show;
                            newEvents[index] = newEvent;

                            dispatch(
                              editProgramma({
                                ...programma,
                                events: newEvents,
                              }),
                            );
                          }}
                        />
                      </Tooltip>

                      <IconButton
                        aria-label="Elimina"
                        variant="solid"
                        icon={<SmallCloseIcon />}
                        colorScheme="red"
                        onClick={() =>
                          dispatch(
                            editProgramma({
                              ...programma,
                              events: programma.events.filter(
                                (e) => e !== event,
                              ),
                            }),
                          )
                        }
                      />
                    </Flex>
                    {event.details?.map((detail, indexDetail) => (
                      <Box
                        key={`${detail.ora} ${detail.description}}`}
                        my={"10px"}
                      >
                        <Tag colorScheme="gray" size="lg">
                          <TagLabel>
                            {detail.ora} · {detail.description}
                          </TagLabel>
                          <TagCloseButton
                            onClick={() => {
                              let newProgramma = { ...programma };
                              const newEventDetails = newProgramma.events[
                                index
                              ].details.filter((d) => d !== detail);

                              let newEventList = [...newProgramma.events];
                              newEventList[index] = {
                                ...newEventList[index],
                              };
                              newEventList[index].details = newEventDetails;
                              newProgramma.events = newEventList;

                              dispatch(editProgramma(newProgramma));
                            }}
                          />
                        </Tag>
                      </Box>
                    ))}
                    <NewEventDetailForm evento={event} programma={programma} />
                    <Divider my="20px" />
                  </Box>
                ))}
                <Box mt="20px">
                  <NewEventForm programma={programma} />
                </Box>
              </CardBody>
              <CardFooter>
                <Button
                  aria-label={programma.show ? "Nascondi" : "Mostra"}
                  variant="solid"
                  colorScheme={programma.show ? "gray" : "green"}
                  onClick={() => dispatch(toggleProgrammaVisibility(programma))}
                  marginRight="20px"
                >
                  {programma.show ? "NASCONDI DAL SITO" : "MOSTRA SUL SITO"}
                </Button>
                <IconButton
                  aria-label="Elimina"
                  variant="solid"
                  icon={<DeleteIcon />}
                  colorScheme="red"
                  onClick={() => dispatch(deleteProgramma(programma))}
                />
              </CardFooter>
            </Card>
          ))}
        <Card w="md" m="10px">
          {isPending && (
            <CardBody>
              <Box w="30px" m="auto">
                <TalesSpinner />
              </Box>
            </CardBody>
          )}
          {!isPending && (
            <form onSubmit={handleSubmit(onSubmit)}>
              <CardHeader>
                <Heading size="base">Aggiungi nuova sezione</Heading>
              </CardHeader>
              <CardBody>
                <FormControl isRequired={true} marginBottom="20px">
                  <FormLabel htmlFor="order">Ordine</FormLabel>
                  <Input id="order" type="number" {...register("order")} />
                  <FormErrorMessage>
                    {errors.title && errors.title.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl marginBottom="20px" isRequired={true}>
                  <FormLabel htmlFor="title">Titolo</FormLabel>
                  <Input
                    placeholder="Sabato 26 Maggio"
                    id="title"
                    type="text"
                    {...register("title", { required: "Campo richiesto" })}
                  />
                  <FormErrorMessage>
                    {errors.title && errors.title.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl marginBottom="20px">
                  <FormLabel htmlFor="subtitle">Sottotitolo</FormLabel>
                  <Input
                    id="subtitle"
                    placeholder="Dalle ore 15 a mezzanotte"
                    type="text"
                    {...register("subtitle")}
                  />
                  <FormErrorMessage>
                    {errors.subtitle && errors.subtitle.message}
                  </FormErrorMessage>
                </FormControl>
                <FormControl>
                  <FormLabel>Mostrare sul sito</FormLabel>
                  <RadioGroup defaultValue={getValues("show").toString()}>
                    <Flex>
                      <Radio
                        value="true"
                        marginRight="30px"
                        {...register("show")}
                      >
                        Sì
                      </Radio>
                      <Radio value="false" {...register("show")}>
                        No
                      </Radio>
                    </Flex>
                  </RadioGroup>
                </FormControl>
              </CardBody>
              <CardFooter>
                <Button
                  type="submit"
                  isDisabled={
                    isPending ||
                    watch("title") === "" ||
                    programmi?.some(
                      (programma) => programma.title === watch("title"),
                    )
                  }
                  colorScheme="green"
                >
                  Crea
                </Button>
              </CardFooter>
            </form>
          )}
        </Card>
      </Wrap>
    </>
  );
}
